import React, {useRef} from 'react'
import {unstable_DialogWrapper as DialogWrapper, DialogInner} from '@reach/dialog'
import VisuallyHidden from '@reach/visually-hidden'
import classNames from 'classnames'
import {Box, Stack, Flex, Text, Button, BoxProps} from '../../vanilla'
import {CloseIcon} from '../icons'
import {
  dialogOverlay,
  dialogContent,
  DialogContentVariants,
  footerCloseButtonContainer,
} from './modal.css'
import useLockBodyScrolling from '../../hooks/use-lock-body-scrolling'

export type ModalProps = {
  error?: boolean
  title?: React.ReactNode
  children?: React.ReactNode
  containerRef?: React.RefObject<Element>
  dialogContentProps?: BoxProps<'div'>
  closeIcon?: boolean
  styleless?: boolean
  allowScroll?: boolean
  footerCloseBtnText?: string
  centerTitle?: boolean
  isOpen: boolean
  stickyHeader?: boolean
  showTitleBorder?: boolean
  dataTestSelector?: string
  onClose: () => void
  onCloseButton?: () => void
} & DialogContentVariants

export const Modal = ({
  error=false,
  title,
  isOpen,
  onClose,
  onCloseButton,
  children,
  size,
  closeIcon = true,
  containerRef,
  dialogContentProps,
  footerCloseBtnText,
  allowScroll = false,
  styleless = false,
  centerTitle,
  stickyHeader = false,
  showTitleBorder = true,
  dataTestSelector
}: ModalProps) => {
  const contentRef = useRef<HTMLDivElement | null>(null)
  const {className: dialogContentClassName, ...dialogContentRestProps} = dialogContentProps || {}

  // Make sure the styles of dialog are only set when the scroll bar is removed
  useLockBodyScrolling(isOpen && !allowScroll)

  return isOpen ? (
    <DialogWrapper containerRef={containerRef}>
      <DialogInner dangerouslyBypassScrollLock={allowScroll}
        className={dialogOverlay({})}
        onDismiss={(event) => {
          if (!contentRef?.current?.contains(event.target as Element)) {
            onClose()
          }
        }}
      >
        <Box
          className={classNames(dialogContent({size: size}), dialogContentClassName)}
          ref={contentRef}
          aria-label="modal"
          data-test-selector={dataTestSelector}
          {...dialogContentRestProps}
        >
          <Stack spacing="0px" bg={styleless ? undefined : 'white'} minHeight="full" height="full">
            {title && (
              <Box
                paddingX="20px"
                paddingY="16px"
                borderBottom={showTitleBorder ? "1px" : "0px"}
                borderColor="gray100"
                {...(stickyHeader ? {position: 'sticky', top: '0px', background: 'white'} : {})}
              >
                <Flex align="center" justify={centerTitle && !closeIcon ? "center" : "space-between"}>
                  <Text variant="heading1" style={{color: error === true ? "red": "initial" }} data-test-selector="modal-title">{title}</Text>
                  {closeIcon && (
                    <Box style={{marginRight: -12, marginTop: -8}}>
                      <Button variant="unstyled" paddingX="0px" width="44px" onClick={onCloseButton ?? onClose} data-test-selector="modal-close-button">
                        <VisuallyHidden>Close</VisuallyHidden>

                        <CloseIcon boxSize="24px" />
                      </Button>
                    </Box>
                  )}
                </Flex>
              </Box>
            )}
            {children}
            {footerCloseBtnText && (
              <Box padding="20px" borderTop="1px" borderColor="gray100">
                <Flex align="center" justify="flex-end">
                  <Box className={footerCloseButtonContainer} width="full">
                    <Button size="sm" variant="dark" onClick={onClose} style={{width: '100%'}}>
                      {footerCloseBtnText}
                    </Button>
                  </Box>
                </Flex>
              </Box>
            )}
          </Stack>
        </Box>
      </DialogInner>
    </DialogWrapper>
  ) : null
}
